import { ReactElement, useEffect, useState } from "react";
import {
  IColumn,
  ICasterProduction,
  BaseType,
  IParentSize, IProductionAggregate
} from "../../../../types";
import {
  formatDate,
  formatDateTimeWithSeconds,
  saveBlobAs,
} from "../../../../system/utils";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from "@mui/material";
import StyledTableRow from "../../../../components/styledTableRow";
import IconButton from "@mui/material/IconButton";
import TableContainer from "../../../../components/tableContainer";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ICatalogCategory, ICategoryField } from "../../../config/types";
import { useAsyncFetch } from "../../../../system/useFetch";

interface IProps {
  productionData: ICasterProduction[];
  parentSize: IParentSize;
}

interface IDataProcessed {
  data: ICasterProduction;
  records: Record<string, number | null>;
  status: Record<string, string>;
}

const columns: IColumn[] = [
  {
    id: "heatNumber",
    label: "Heat",
    minWidth: 80,
  },
  {
    id: "steel",
    label: "Steel",
    minWidth: 120,
  },
  {
    id: "sequence",
    label: "Sequence",
    minWidth: 80,
  },
  {
    id: "factoryDay",
    label: "Factory Day",
    minWidth: 120,
    format: value => formatDate(value),
  },
  {
    id: "shiftName",
    label: "Shift",
    minWidth: 80,
  },
  {
    id: "crew",
    label: "Crew",
    minWidth: 80
  },
  {
    id: "beginDatetime",
    label: "Begin Datetime",
    minWidth: 170,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "endDatetime",
    label: "End Datetime",
    minWidth: 170,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "ladle",
    label: "Ladle",
    minWidth: 80,
  }
];

const subCols = [
  { id: "super_heat_spec_min", label: "MIN", backgroundColor: "rgba(159,168,218, .1)" },
  { id: "super_heat_spec_max", label: "MAX", backgroundColor: "rgba(159,168,218, .1)" },
  { id: "super_heat_general_min", label: "MIN", backgroundColor:  "rgba(159,168,218, .2)", statusKey: "super_heat_general_status" },
  { id: "super_heat_general_avg", label: "AVG", backgroundColor: "rgba(159,168,218, .2)", statusKey: "super_heat_general_status" },
  { id: "super_heat_general_max", label: "MAX", backgroundColor: "rgba(159,168,218, .2)", statusKey: "super_heat_general_status" },
  { id: "super_heat_general_std_dev", label: "STD DEV", backgroundColor: "rgba(159,168,218, .2)", statusKey: "super_heat_general_status" },

  { id: "speed_spec_min", label: "MIN", backgroundColor: "rgba(144,202,249, .1)" },
  { id: "speed_spec_max", label: "MAX", backgroundColor: "rgba(144,202,249, .1)" },
  { id: "speed_s1_min", label: "MIN", backgroundColor: "rgba(144,202,249, .2)", statusKey: "speed_s1_status" },
  { id: "speed_s1_avg", label: "AVG", backgroundColor: "rgba(144,202,249, .2)", statusKey: "speed_s1_status" },
  { id: "speed_s1_max", label: "MAX", backgroundColor: "rgba(144,202,249, .2)", statusKey: "speed_s1_status" },
  { id: "speed_s1_std_dev", label: "STD DEV", backgroundColor: "rgba(144,202,249, .2)", statusKey: "speed_s1_status" },
  { id: "speed_s2_min", label: "MIN", backgroundColor: "rgba(144,202,249, .3)", statusKey: "speed_s2_status" },
  { id: "speed_s2_avg", label: "AVG", backgroundColor: "rgba(144,202,249, .3)", statusKey: "speed_s2_status" },
  { id: "speed_s2_max", label: "MAX", backgroundColor: "rgba(144,202,249, .3)", statusKey: "speed_s2_status" },
  { id: "speed_s2_std_dev", label: "STD DEV", backgroundColor: "rgba(144,202,249, .3)", statusKey: "speed_s2_status" },
  { id: "speed_s3_min", label: "MIN", backgroundColor: "rgba(144,202,249, .4)", statusKey: "speed_s3_status" },
  { id: "speed_s3_avg", label: "AVG", backgroundColor: "rgba(144,202,249, .4)", statusKey: "speed_s3_status" },
  { id: "speed_s3_max", label: "MAX", backgroundColor: "rgba(144,202,249, .4)", statusKey: "speed_s3_status" },
  { id: "speed_s3_std_dev", label: "STD DEV", backgroundColor: "rgba(144,202,249, .4)", statusKey: "speed_s3_status" },
  { id: "speed_s4_min", label: "MIN", backgroundColor: "rgba(144,202,249, .5)", statusKey: "speed_s4_status" },
  { id: "speed_s4_avg", label: "AVG", backgroundColor: "rgba(144,202,249, .5)", statusKey: "speed_s4_status" },
  { id: "speed_s4_max", label: "MAX", backgroundColor: "rgba(144,202,249, .5)", statusKey: "speed_s4_status" },
  { id: "speed_s4_std_dev", label: "STD DEV", backgroundColor: "rgba(144,202,249, .5)", statusKey: "speed_s4_status" },

  { id: "mems_spec_min", label: "MIN", backgroundColor: "rgba(128,222,234, .1)" },
  { id: "mems_spec_max", label: "MAX", backgroundColor: "rgba(128,222,234, .1)" },
  { id: "mems_s1_min", label: "MIN", backgroundColor: "rgba(128,222,234, .2)", statusKey: "mems_s1_status" },
  { id: "mems_s1_avg", label: "AVG", backgroundColor: "rgba(128,222,234, .2)", statusKey: "mems_s1_status" },
  { id: "mems_s1_max", label: "MAX", backgroundColor: "rgba(128,222,234, .2)", statusKey: "mems_s1_status" },
  { id: "mems_s1_std_dev", label: "STD DEV", backgroundColor: "rgba(128,222,234, .2)", statusKey: "mems_s1_status" },
  { id: "mems_s2_min", label: "MIN", backgroundColor: "rgba(128,222,234, .3)", statusKey: "mems_s2_status" },
  { id: "mems_s2_avg", label: "AVG", backgroundColor: "rgba(128,222,234, .3)", statusKey: "mems_s2_status" },
  { id: "mems_s2_max", label: "MAX", backgroundColor: "rgba(128,222,234, .3)", statusKey: "mems_s2_status" },
  { id: "mems_s2_std_dev", label: "STD DEV", backgroundColor: "rgba(128,222,234, .3)", statusKey: "mems_s2_status" },
  { id: "mems_s3_min", label: "MIN", backgroundColor: "rgba(128,222,234, .4)", statusKey: "mems_s3_status" },
  { id: "mems_s3_avg", label: "AVG", backgroundColor: "rgba(128,222,234, .4)", statusKey: "mems_s3_status" },
  { id: "mems_s3_max", label: "MAX", backgroundColor: "rgba(128,222,234, .4)", statusKey: "mems_s3_status" },
  { id: "mems_s3_std_dev", label: "STD DEV", backgroundColor: "rgba(128,222,234, .4)", statusKey: "mems_s3_status" },
  { id: "mems_s4_min", label: "MIN", backgroundColor: "rgba(128,222,234, .5)", statusKey: "mems_s4_status" },
  { id: "mems_s4_avg", label: "AVG", backgroundColor: "rgba(128,222,234, .5)", statusKey: "mems_s4_status" },
  { id: "mems_s4_max", label: "MAX", backgroundColor: "rgba(128,222,234, .5)", statusKey: "mems_s4_status" },
  { id: "mems_s4_std_dev", label: "STD DEV", backgroundColor: "rgba(128,222,234, .5)", statusKey: "mems_s4_status" },

  { id: "fems_spec_min", label: "MIN", backgroundColor: "rgba(128,203,196, .1)" },
  { id: "fems_spec_max", label: "MAX", backgroundColor: "rgba(128,203,196, .1)" },
  { id: "fems_s1_min", label: "MIN", backgroundColor: "rgba(128,203,196, .2)", statusKey: "fems_s1_status" },
  { id: "fems_s1_avg", label: "AVG", backgroundColor: "rgba(128,203,196, .2)", statusKey: "fems_s1_status" },
  { id: "fems_s1_max", label: "MAX", backgroundColor: "rgba(128,203,196, .2)", statusKey: "fems_s1_status" },
  { id: "fems_s1_std_dev", label: "STD DEV", backgroundColor: "rgba(128,203,196, .2)", statusKey: "fems_s1_status" },
  { id: "fems_s2_min", label: "MIN", backgroundColor: "rgba(128,203,196, .3)", statusKey: "fems_s2_status" },
  { id: "fems_s2_avg", label: "AVG", backgroundColor: "rgba(128,203,196, .3)", statusKey: "fems_s2_status" },
  { id: "fems_s2_max", label: "MAX", backgroundColor: "rgba(128,203,196, .3)", statusKey: "fems_s2_status" },
  { id: "fems_s2_std_dev", label: "STD DEV", backgroundColor: "rgba(128,203,196, .3)", statusKey: "fems_s2_status" },
  { id: "fems_s3_min", label: "MIN", backgroundColor: "rgba(128,203,196, .4)", statusKey: "fems_s3_status" },
  { id: "fems_s3_avg", label: "AVG", backgroundColor: "rgba(128,203,196, .4)", statusKey: "fems_s3_status" },
  { id: "fems_s3_max", label: "MAX", backgroundColor: "rgba(128,203,196, .4)", statusKey: "fems_s3_status" },
  { id: "fems_s3_std_dev", label: "STD DEV", backgroundColor: "rgba(128,203,196, .4)", statusKey: "fems_s3_status" },
  { id: "fems_s4_min", label: "MIN", backgroundColor: "rgba(128,203,196, .5)", statusKey: "fems_s4_status" },
  { id: "fems_s4_avg", label: "AVG", backgroundColor: "rgba(128,203,196, .5)", statusKey: "fems_s4_status" },
  { id: "fems_s4_max", label: "MAX", backgroundColor: "rgba(128,203,196, .5)", statusKey: "fems_s4_status" },
  { id: "fems_s4_std_dev", label: "STD DEV", backgroundColor: "rgba(128,203,196, .5)", statusKey: "fems_s4_status" },

  { id: "swv_spec_min", label: "MIN", backgroundColor: "rgba(165,214,167, .1)" },
  { id: "swv_spec_max", label: "MAX", backgroundColor: "rgba(165,214,167, .1)" },
  { id: "swv_s1_min", label: "MIN", backgroundColor: "rgba(165,214,167, .2)", statusKey: "swv_s1_status" },
  { id: "swv_s1_avg", label: "AVG", backgroundColor: "rgba(165,214,167, .2)", statusKey: "swv_s1_status" },
  { id: "swv_s1_max", label: "MAX", backgroundColor: "rgba(165,214,167, .2)", statusKey: "swv_s1_status" },
  { id: "swv_s1_std_dev", label: "STD DEV", backgroundColor: "rgba(165,214,167, .2)", statusKey: "swv_s1_status" },
  { id: "swv_s2_min", label: "MIN", backgroundColor: "rgba(165,214,167, .3)", statusKey: "swv_s2_status" },
  { id: "swv_s2_avg", label: "AVG", backgroundColor: "rgba(165,214,167, .3)", statusKey: "swv_s2_status" },
  { id: "swv_s2_max", label: "MAX", backgroundColor: "rgba(165,214,167, .3)", statusKey: "swv_s2_status" },
  { id: "swv_s2_std_dev", label: "STD DEV", backgroundColor: "rgba(165,214,167, .3)", statusKey: "swv_s2_status" },
  { id: "swv_s3_min", label: "MIN", backgroundColor: "rgba(165,214,167, .4)", statusKey: "swv_s3_status" },
  { id: "swv_s3_avg", label: "AVG", backgroundColor: "rgba(165,214,167, .4)", statusKey: "swv_s3_status" },
  { id: "swv_s3_max", label: "MAX", backgroundColor: "rgba(165,214,167, .4)", statusKey: "swv_s3_status" },
  { id: "swv_s3_std_dev", label: "STD DEV", backgroundColor: "rgba(165,214,167, .4)", statusKey: "swv_s3_status" },
  { id: "swv_s4_min", label: "MIN", backgroundColor: "rgba(165,214,167, .5)", statusKey: "swv_s4_status" },
  { id: "swv_s4_avg", label: "AVG", backgroundColor: "rgba(165,214,167, .5)", statusKey: "swv_s4_status" },
  { id: "swv_s4_max", label: "MAX", backgroundColor: "rgba(165,214,167, .5)", statusKey: "swv_s4_status" },
  { id: "swv_s4_std_dev", label: "STD DEV", backgroundColor: "rgba(165,214,167, .5)", statusKey: "swv_s4_status" },

];

interface IRowProps {
  index: number;
  row: IDataProcessed;
}

// const getFieldData = (aggregate: IProductionAggregate, fields: ICategoryField[]): ICategoryField | null => {
//
//   const getFieldDataInternal = (variable: string, fieldName: string) => {
//     if (aggregate.code.includes(variable)) {
//       const field = fields.find(x => x.name === fieldName);
//
//       return field ?? null;
//     }
//
//     return null;
//   };
//
//   return getFieldDataInternal(".MEMS.", "MEMS")
//     || getFieldDataInternal(".FEMS.", "FEMS")
//     || getFieldDataInternal(".AUTO_SUPER_HEAT", "Superheat")
//     || getFieldDataInternal(".SWV_CALCULATION", "Secondary Cooling(SWV)")
//     || getFieldDataInternal(".SPEED_ACTUAL", "Casting Speed")
//     || null;
// };

const processStatus = (
  row: ICasterProduction,
  variable: string,
  fields: ICategoryField[],
  fieldName: string,
  records: Record<string, number | null>,
  status: Record<string, string>,
  prefix: string): void => {
  const aggregates = row.productionAggregates.filter(x => x.code.includes(variable));
  const field = fields.find(x => x.name === fieldName);

  if (!field) {
    return;
  }

  records[`${prefix}_spec_min`] = field.minimum ?? null;
  records[`${prefix}_spec_max`] = field.maximum ?? null;

  aggregates.forEach(aggregate => {
    if (aggregate) {
      const strand = aggregate.group.toLowerCase();
      const recordKey = `${prefix}_${strand}_status`;
      const comparison = field.customOptions && field.customOptions.params && field.customOptions.params.comparison_field
        ? field.customOptions.params.comparison_field.value ?? undefined
        : undefined;

      const key = comparison as keyof IProductionAggregate | undefined;
      if (key && aggregate[key] !== undefined && aggregate[key] !== null) {
        const value = aggregate[key];
        if (typeof value === "number") {
          if (field.maximum && key && value > field.maximum) {
            status[recordKey] = `${fieldName} = ${value} > ${field.maximum}`;
          } else if (field.minimum && key && value < field.minimum) {
            status[recordKey] = `${fieldName} = ${value} < ${field.minimum}`;
          }
        }
      } else {
        if (field.maximum && aggregate.max && aggregate.max > field.maximum) {
          status[recordKey] = `${fieldName} = ${aggregate.max} > ${field.maximum}`;
        } else if (field.minimum && aggregate.min && aggregate.min < field.minimum) {
          status[recordKey] = `${fieldName} = ${aggregate.min} < ${field.minimum}`;
        }
      }
    }
  });
};

const RowComponent = ({ row, index }: IRowProps): ReactElement => {
  const dataProcessed = row.data;

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1}>
      {columns.map((column: IColumn) => {
        const key = `${dataProcessed.heatNumber}-${column.id}`;

        if (column.id === "heatNumber") {
          return (
            <TableCell key={key} align={column.align} style={{ position: "sticky", left: 0, backgroundColor: "#9caab1" }}>
              {dataProcessed.heatNumber}
            </TableCell>
          );
        }

        if (column.id === "steel") {
          return (
            <TableCell key={key}  align={column.align} style={{ position: "sticky", left: 88, backgroundColor: "#9caab1" }}>
              {dataProcessed.steel.code}
            </TableCell>
          );
        }

        if (column.id === "factoryDay") {
          return (
            <TableCell key={key} align={column.align}>
              {column.format ? column.format(dataProcessed.shiftHistory.factoryDay) : ""}
            </TableCell>
          );
        }

        if (column.id === "shiftName") {
          return (
            <TableCell key={key}  align={column.align}>
              {dataProcessed.shiftHistory.name}
            </TableCell>
          );
        }

        if (column.id === "crew") {
          return (
            <TableCell key={key}  align={column.align}>
              {dataProcessed.crew.name}
            </TableCell>
          );
        }

        if (column.id === "ladle") {
          return (
            <TableCell key={key}  align={column.align}>
              {dataProcessed.ladleHistory && dataProcessed.ladleHistory.ladle}
            </TableCell>
          );
        }

        const value = dataProcessed[column.id as keyof ICasterProduction] as BaseType;

        return (
          <TableCell key={key}  align={column.align}>
            {column.format && typeof value === "number" ? column.format(value) : value}
          </TableCell>
        );
      })}

      { subCols.map(column => {
        const key = `${column.id}_${index}`;
        const status = column.statusKey ? row.status[column.statusKey] : undefined;

        return (
          <TableCell key={key}  align="right" style={{ backgroundColor: status ? "red" : column.backgroundColor }} >
            {row.records[column.id]?.toFixed(2)}
          </TableCell>
        );
      })}
    </StyledTableRow>
  );

};

const ProductionRawTable = ({ productionData, parentSize }: IProps): ReactElement => {
  const [ dataProcessed, setDataProcessed ] = useState<IDataProcessed[]>( []);
  const [ getCatalogs ] = useAsyncFetch<Record<string, ICatalogCategory[]>>();
  const [ specDictionary, setSpecDictionary ] = useState<Record<string, ICatalogCategory[]>>({});
  const [ specDictStatus, setSpecDictStatus ] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const process = async  () => {
      const steelGrades = new Set<string>();
      const newStatus: Record<string, boolean> = { ...specDictStatus };

      productionData.forEach(pd => {
        if (!specDictionary[pd.steel.code] && specDictStatus[pd.steel.code] === undefined) {
          steelGrades.add(pd.steel.code);
          newStatus[pd.steel.code] = false;
        }
      });

      if (steelGrades.size > 0) {
        const response = await getCatalogs({
          module: "catalog",
          query: "getCatalogFromProducts",
          payload: {
            areaCode: "MELTSHOP",
            zoneCode: "CASTER",
            catalogName: "Carbon Segregation",
            productNames: [...steelGrades]
          }
        });

        if (response) {
          Object.keys(response).forEach(grade => newStatus[grade] = true);

          setSpecDictionary(prevState => ({ ...prevState, ...response }));
          setSpecDictStatus(newStatus);
        }
      }
    };

    process().then();
  }, [productionData, specDictionary, specDictStatus]);

  useEffect(() => {
    const newData: IDataProcessed[] = [];

    const processVariable = (row: ICasterProduction, variable: string, prefix: string, records: Record<string, number | null>) => {
      const items = row.productionAggregates.filter(x => x.code.includes(variable));

      items.forEach(item => {
        const strand = item.group.toLowerCase();

        records[`${prefix}_${strand}_min`] = item?.min ?? null;
        records[`${prefix}_${strand}_avg`] = item?.avg ?? null;
        records[`${prefix}_${strand}_max`] = item?.max ?? null;
        records[`${prefix}_${strand}_std_dev`] = item?.desStd ?? null;
      });
    };

    for (const data of productionData) {
      const records: Record<string, number | null> = {};
      const status: Record<string, string> = {};

      const spec = specDictionary[data.steel.code] && specDictionary[data.steel.code].length > 0
        ? specDictionary[data.steel.code][0].content.fields
        : [];

      processVariable(data, ".AUTO_SUPER_HEAT", "super_heat", records);
      processVariable(data, ".SPEED_ACTUAL", "speed", records);
      processVariable(data, ".MEMS.", "mems", records);
      processVariable(data, ".FEMS.", "fems", records);
      processVariable(data, ".SWV_CALCULATION", "swv", records);

      processStatus(data, ".AUTO_SUPER_HEAT", spec, "Superheat", records, status, "super_heat");
      processStatus(data, ".SPEED_ACTUAL", spec, "Casting Speed", records, status, "speed");
      processStatus(data, ".MEMS.", spec, "MEMS", records, status, "mems");
      processStatus(data, ".FEMS.", spec, "FEMS", records, status, "fems");
      processStatus(data, ".SWV_CALCULATION", spec, "Secondary Cooling(SWV)", records, status, "swv");

      newData.push({
        data,
        records,
        status
      });
    }

    setDataProcessed(newData);
  }, [ productionData, specDictionary ]);

  const onExportData = () => {
    let fileContent = "";

    fileContent += ";;;;;;;;;Super Heat;;;;;;Speed;;;;;;;;;;;;;;;;;;MEMS;;;;;;;;;;;;;;;;;;FEMS;;;;;;;;;;;;;;;;;;SWV;;;;;;;;;;;;;;;;;;\n";
    fileContent += ";;;;;;;;;Spec;;General;;;;Spec;;Strand 1;;;;Strand 2;;;;Strand 3;;;;Strand 4;;;;Spec;;Strand 1;;;;Strand 2;;;;Strand 3;;;;Strand 4;;;;";
    fileContent += "Spec;;Strand 1;;;;Strand 2;;;;Strand 3;;;;Strand 4;;;;Spec;;Strand 1;;;;Strand 2;;;;Strand 3;;;;Strand 4;;;;\n";
    fileContent += "Heat;Steel;Sequence;Factory Day;Shift;Crew;Begin Datetime;End Datetime;Ladle;";
    fileContent += "MIN;MAX;MIN;AVG;MAX;STD DEV;";
    fileContent += "MIN;MAX;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;";
    fileContent += "MIN;MAX;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;";
    fileContent += "MIN;MAX;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;";
    fileContent += "MIN;MAX;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;MIN;AVG;MAX;STD DEV;\n";

    dataProcessed.forEach(row => {
      const { data, records } = row;

      fileContent += `${data.heatNumber};${data.steel.code};${data.sequence};${data.shiftHistory.factoryDay};`;
      fileContent += `${data.shiftHistory.name};${data.crew.name};${formatDateTimeWithSeconds(data.beginDatetime)};`;
      fileContent += `${formatDateTimeWithSeconds(data.endDatetime)};${data.ladleHistory ? data.ladleHistory.ladle : ""};`;

      subCols.forEach(column => {
        const value = records[column.id];
        fileContent += `${value ? value.toFixed(2) : ""};`;
      });

      fileContent += "\n";
    });

    const file = new Blob([ fileContent ], { type: "text/csv" });

    saveBlobAs(URL.createObjectURL(file), "caster-raw-data-qr.csv");
  };

  const tableContainerHeight = parentSize.height - 48;

  return (
    <>
      <TableContainer className="flex-grow-1" style={{ maxHeight: tableContainerHeight, height: tableContainerHeight }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead style={{ backgroundColor: "#b0bec5" }}>
            <TableRow >
              <TableCell colSpan={2} style={{ backgroundColor: "#9caab1", zIndex: 999, position: "sticky", left: 0 }}/>
              <TableCell colSpan={7} style={{ backgroundColor: "#b0bec5" }}/>
              <TableCell colSpan={6} align="center" style={{ backgroundColor: "rgba(159,168,218, .5)" }}>Super Heat</TableCell>
              <TableCell colSpan={18} align="center" style={{ backgroundColor: "rgba(144,202,249, .5)" }}>Speed</TableCell>
              <TableCell colSpan={18} align="center" style={{ backgroundColor: "rgba(128,222,234, .5)" }}>MEMS</TableCell>
              <TableCell colSpan={18} align="center" style={{ backgroundColor: "rgba(128,203,196, .5)" }}>FEMS</TableCell>
              <TableCell colSpan={18} align="center" style={{ backgroundColor: "rgba(165,214,167, .5)" }}>SWV</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={2} style={{ backgroundColor: "#9caab1", zIndex: 999, position: "sticky", left: 0 }}/>
              <TableCell colSpan={7} style={{ backgroundColor: "#b0bec5" }}/>
              <TableCell colSpan={2} align="center" style={{ backgroundColor: "rgba(159,168,218, .1)" }} >Spec</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(159,168,218, .2)" }}>General</TableCell>
              <TableCell colSpan={2} align="center" style={{ backgroundColor: "rgba(144,202,249, .1)" }} >Spec</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(144,202,249, .2)" }}>Strand 1</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(144,202,249, .3)" }}>Strand 2</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(144,202,249, .4)" }}>Strand 3</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(144,202,249, .5)" }}>Strand 4</TableCell>
              <TableCell colSpan={2} align="center" style={{ backgroundColor: "rgba(128,222,234, .1)" }} >Spec</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,222,234, .2)" }}>Strand 1</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,222,234, .3)" }}>Strand 2</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,222,234, .4)" }}>Strand 3</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,222,234, .5)" }}>Strand 4</TableCell>
              <TableCell colSpan={2} align="center" style={{ backgroundColor: "rgba(128,203,196, .1)" }} >Spec</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,203,196, .2)" }}>Strand 1</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,203,196, .3)" }}>Strand 2</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,203,196, .4)" }}>Strand 3</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(128,203,196, .5)" }}>Strand 4</TableCell>
              <TableCell colSpan={2} align="center" style={{ backgroundColor: "rgba(165,214,167, .1)" }} >Spec</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(165,214,167, .2)" }}>Strand 1</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(165,214,167, .3)" }}>Strand 2</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(165,214,167, .4)" }}>Strand 3</TableCell>
              <TableCell colSpan={4} align="center" style={{ backgroundColor: "rgba(165,214,167, .5)" }}>Strand 4</TableCell>
            </TableRow>
            <TableRow>
              {columns.map((column, index) =>
                <TableCell
                  key={`${column.id}-${index}`}
                  align={column.align}
                  style={{
                    backgroundColor: index < 2 ? "#9caab1" : "#b0bec5",
                    minWidth: column.minWidth,
                    zIndex: index < 2 ? 999 : undefined,
                    position: index < 2 ? "sticky" : undefined,
                    left: index < 2 ? index * 88 : undefined
                  }}>
                  {column.label}
                </TableCell>
              )}
              {subCols.map(c => (
                <TableCell
                  key={c.id}
                  align="right"
                  style={{ backgroundColor: "#b0bec5", minWidth: 90 }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataProcessed.map((row: IDataProcessed, index: number) => {

              return (
                <RowComponent key={`prod-eaf-row-${index}`} row={row} index={index} />
              );
            })}
          </TableBody>
        </Table>

        {!productionData &&
          <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2">
            <CircularProgress className=""/>
          </Paper>
        }
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>
              <IconButton color="primary" onClick={onExportData} disabled={!productionData}>
                <Tooltip title={"Export data"}>
                  <FileDownloadIcon/>
                </Tooltip>
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default ProductionRawTable;
